import React, { useState, useEffect } from "react";
import axios from "axios";
import { baseurl } from "../../Baseurl";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../api/axiosInstance";
export default function AddInsurance() {
  const navigate = useNavigate();
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [patientsLoading, setPatientsLoading] = useState(false);
  const [patientsError, setPatientsError] = useState(false);
  const [patientList, setPatientList] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesError, setInvoicesError] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    company: "",
    patient_id: "",
    invoice_id: "",
    services: "",
    amount: ""
  });
  useEffect(() => {
    const fetchPatients = async () => {
      try {
        setPatientsLoading(true);
        setPatientsError(false);
        const res = await axiosInstance.get(`getActivePatients`);
        if (res.data?.success && Array.isArray(res.data.data)) {
          setPatientList(res.data.data);
        } else {
          setPatientsError(true);
        }
      } catch (err) {
        setPatientsError(true);
      } finally {
        setPatientsLoading(false);
      }
    };
    fetchPatients();
  }, []);
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (name === 'patient_id') {
      setFormData((prev) => ({ ...prev, invoice_id: "", amount: prev.amount }));
      if (value) fetchInvoicesForPatient(value);
      else { setInvoices([]); }
    }
    if (name === 'invoice_id') {
      const selected = invoices.find(inv => String(inv.invoiceId || inv.invoice_id) === String(value));
      if (selected) {
        const remaining = Number(selected.remaining_amount ?? selected.remainingAmount ?? 0);
        setFormData((prev) => ({ ...prev, amount: String(remaining) }));
      }
    }
  };
  const fetchInvoicesForPatient = async (patientId) => {
    try {
      setInvoicesLoading(true);
      setInvoicesError(false);
      const res = await axiosInstance.get(`getRemainingInvoices/${patientId}`);
      // Be tolerant to different API response shapes (with/without success flag)
      const rows = Array.isArray(res.data?.invoices)
        ? res.data.invoices
        : Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
            ? res.data
            : [];
      const normalized = rows.map(r => ({
        invoice_id: r.invoiceId ?? r.invoice_id ?? r.id,
        invoice_no: r.invoice_no ?? r.invoiceNo ?? `INV-${r.invoiceId ?? r.id}`,
        invoice_date: r.invoice_date ?? r.invoiceDate,
        remaining_amount: r.remaining_amount ?? r.remainingAmount ?? 0
      }));
      setInvoices(normalized);
      setInvoicesError(false);
    } catch (err) {
      setInvoices([]);
      setInvoicesError(true);
    } finally {
      setInvoicesLoading(false);
    }
  };
  const validate = () => {
    const err = {};
    if (!formData.date.trim()) err.date = "Date is required";
    if (!formData.company.trim()) err.company = "Company is required";
    if (!formData.patient_id.trim()) err.patient_id = "Patient is required";
    if (!formData.services.trim()) err.services = "Services are required";
    if (!formData.amount || isNaN(formData.amount) || formData.amount <= 0) err.amount = "Amount must be a positive number";
    return err;
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const foundErrors = validate();
    if (Object.keys(foundErrors).length > 0) {
      setErrors(foundErrors);
      setLoading(false);
      return;
    }
    try {
      const response = await axiosInstance.post(`addInsurance`, formData, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (response.data.success) {
        Swal.fire("Success", "Insurance claim added successfully!", "success");
        navigate("/Admin/Insurance");
      } else {
        Swal.fire("Error", response.data.message || "Something went wrong", "error");
      }
    } catch (error) {
      Swal.fire("Error", "Server error while adding insurance claim", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="pc-container">
      <div className="pc-content">
        <div className="col-12">
          <div className="card table-card">
            <div className="tableHeader">
              <h5 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span
                  style={{ cursor: 'pointer', color: '#3f75c7', fontWeight: 'bold', fontSize: '1.5rem' }}
                  onClick={() => navigate(-1)}
                  title="Back"
                >
                  &larr;
                </span>
                Add Insurance Claim
              </h5>
            </div>

            <form
              onSubmit={handleSubmit}
              className="row g-3 px-3 py-2 mb-3"
            >
              {/* Date */}
              <div className="col-md-6">
                <label className="form-label">Date</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  className="form-control"
                />
                {errors.date && (
                  <p className="text-danger">{errors.date}</p>
                )}
              </div>

              {/* Company */}
              <div className="col-md-6">
                <label className="form-label">Company</label>
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter insurance company name"
                />
                {errors.company && (
                  <p className="text-danger">{errors.company}</p>
                )}
              </div>

              {/* Patient (Dropdown) */}
              <div className="col-md-6">
                <label className="form-label">Patient</label>
                <select
                  className="form-control"
                  name="patient_id"
                  value={formData.patient_id}
                  onChange={handleChange}
                  disabled={patientsLoading}
                >
                  <option value="">{patientsLoading ? "Loading patients..." : "Select Patient"}</option>
                  {!patientsLoading && !patientsError && patientList.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.firstName} {p.lastName}
                    </option>
                  ))}
                </select>
                {patientsError && (
                  <p className="text-danger">Failed to load patients. Please try again.</p>
                )}
                {errors.patient_id && (
                  <p className="text-danger">{errors.patient_id}</p>
                )}
              </div>

              {/* Invoice (Dropdown) */}
              <div className="col-md-6">
                <label className="form-label">Invoice</label>
                <select
                  className="form-control"
                  name="invoice_id"
                  value={formData.invoice_id}
                  onChange={handleChange}
                  disabled={!formData.patient_id || invoicesLoading}
                >
                  <option value="">{!formData.patient_id ? "Select patient first" : (invoicesLoading ? "Loading invoices..." : "Select Invoice")}</option>
                  {!invoicesLoading && !invoicesError && invoices.map((inv) => (
                    <option key={inv.invoice_id} value={inv.invoice_id}>
                      {inv.invoice_no} — Remaining: {Number(inv.remaining_amount).toFixed(2)}
                    </option>
                  ))}
                </select>
                {invoicesError && formData.patient_id && (
                  <p className="text-danger">Failed to load invoices for this patient.</p>
                )}
              </div>

              {/* Services */}
              <div className="col-md-6">
                <label className="form-label">Services</label>
                <input
                  type="text"
                  name="services"
                  value={formData.services}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter services (e.g., X-Ray Chest)"
                />
                {errors.services && (
                  <p className="text-danger">{errors.services}</p>
                )}
              </div>

              {/* Amount */}
              <div className="col-md-6">
                <label className="form-label">Amount</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter amount"
                  min="0"
                  step="0.01"
                />
                {errors.amount && (
                  <p className="text-danger">{errors.amount}</p>
                )}
              </div>

              <div className="col-12 mb-3 text-center">
                <button
                  type="submit"
                  className="bgBtn"
                  disabled={loading}
                >
                  {loading ? "Adding..." : "Add Insurance Claim"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
